/**
 * Breadcrumbs — accessible navigation breadcrumb trail.
 *
 * Renders an ordered list of named locations with a configurable
 * separator. The final crumb is rendered as the current page
 * (`aria-current="page"`) and never as a link; earlier crumbs are
 * rendered as either anchor tags (when `href` is supplied) or as
 * buttons (when only `onSelect` is supplied), so the same component
 * adapts to in-app routers and plain link navigation without forcing
 * the consumer to choose.
 *
 * Single responsibility: render a typed crumb trail with consistent
 * semantics; the trail's data model and navigation behavior belong
 * to the host application.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface BreadcrumbItem {
  /** Stable identifier used for React reconciliation. */
  readonly id: string;
  /** Visible label. */
  readonly label: string;
  /** Optional anchor href — when present the crumb renders as an `<a>`. */
  readonly href?: string;
  /**
   * Optional click handler — when present (and `href` is absent) the
   * crumb renders as a `<button>` so SPA routers can intercept the
   * navigation themselves.
   */
  readonly onSelect?: () => void;
}

export interface BreadcrumbsProps {
  /** Ordered crumbs from root to current page. */
  readonly items: readonly BreadcrumbItem[];
  /** Separator rendered between crumbs. Defaults to `/`. */
  readonly separator?: React.ReactNode;
  /** Accessible label for the surrounding `<nav>`. */
  readonly ariaLabel?: string;
  /** Additional className appended to the outer `<nav>`. */
  readonly className?: string;
}

const SEPARATOR_STYLE: React.CSSProperties = {
  color: GRAY[400],
  margin: "0 0.4rem",
  userSelect: "none",
};

const CRUMB_STYLE: React.CSSProperties = {
  color: PRIMARY[700],
  fontSize: "0.8125rem",
  textDecoration: "none",
};

const CURRENT_STYLE: React.CSSProperties = {
  color: GRAY[900],
  fontSize: "0.8125rem",
  fontWeight: 600,
};

interface CrumbLinkProps {
  readonly item: BreadcrumbItem;
}

function CrumbLink({ item }: CrumbLinkProps): React.JSX.Element {
  if (item.href !== undefined) {
    return (
      <a href={item.href} style={CRUMB_STYLE} data-csk-breadcrumb-link>
        {item.label}
      </a>
    );
  }
  if (item.onSelect !== undefined) {
    return (
      <button
        type="button"
        onClick={item.onSelect}
        style={{ ...CRUMB_STYLE, background: "none", border: 0, padding: 0, cursor: "pointer" }}
        data-csk-breadcrumb-link
      >
        {item.label}
      </button>
    );
  }
  return (
    <span style={CRUMB_STYLE} data-csk-breadcrumb-link>
      {item.label}
    </span>
  );
}

/**
 * Renders an ordered breadcrumb trail. The final crumb is marked
 * `aria-current="page"` and rendered as inert text — links and
 * buttons are reserved for earlier crumbs.
 */
export const Breadcrumbs = React.memo(function Breadcrumbs({
  items,
  separator = "/",
  ariaLabel = "Breadcrumb",
  className,
}: BreadcrumbsProps): React.JSX.Element {
  return (
    <nav aria-label={ariaLabel} className={cn("font-body", className)}>
      <ol
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={item.id} style={{ display: "inline-flex", alignItems: "center" }}>
              {isLast ? (
                <span aria-current="page" style={CURRENT_STYLE} data-csk-breadcrumb-current>
                  {item.label}
                </span>
              ) : (
                <CrumbLink item={item} />
              )}
              {!isLast && (
                <span aria-hidden="true" style={SEPARATOR_STYLE}>
                  {separator}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});
